import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import { Search, Plus, ChevronLeft, Clock, CheckCircle2, AlertCircle, Loader2, Trash2, ChevronRight, Globe2, ListTree } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  uploaded: { label: "Hazırlanıyor", color: "ky-badge-neutral", icon: Clock },
  preparing: { label: "Başlatılıyor", color: "ky-badge-neutral", icon: Loader2 },
  analyzing: { label: "Analiz ediliyor", color: "ky-badge-warning", icon: Loader2 },
  finalizing: { label: "Tamamlanıyor", color: "ky-badge-warning", icon: Loader2 },
  done: { label: "Tamamlandı", color: "ky-badge-success", icon: CheckCircle2 },
  failed: { label: "Başarısız", color: "ky-badge-danger", icon: AlertCircle },
};

export default function KeywordToolPage() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/ai-tools/keyword/jobs");
      setJobs(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const remove = async (id) => {
    if (!window.confirm("Bu analizi silmek istediğine emin misin?")) return;
    try { await apiClient.delete(`/ai-tools/keyword/jobs/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="keyword-tool-page">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/ai-araclar" className="hover:text-[#4F46E5] flex items-center gap-1"><ChevronLeft size={14}/>AI Araçlar</Link>
        <span>/</span>
        <span className="font-medium text-[#0F172A]">Anahtar Kelime Analizi</span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#EEF2FF] flex items-center justify-center shrink-0">
            <Search size={20} className="text-[#4F46E5]"/>
          </div>
          <div>
            <h1 className="text-2xl font-bold">Anahtar Kelime Analizi</h1>
            <p className="text-sm text-[#64748B] mt-1 max-w-2xl">CSV yükle, müşteri profilini gir, AI senin için pozitif/negatif anahtar kelime listeleri üretsin.</p>
          </div>
        </div>
        <button onClick={() => nav("/ai-araclar/anahtar-kelime/yeni")} className="ky-btn ky-btn-accent" data-testid="kw-new">
          <Plus size={14}/>Yeni Analiz
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={()=>nav("/ai-araclar/anahtar-kelime/listeler")} className="ky-btn ky-btn-outline ky-btn-sm" data-testid="open-lists">
          <ListTree size={13}/>Kayıtlı Listeler
        </button>
      </div>

      <div className="ky-card p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-[#ECECEF] flex items-center justify-between">
          <h3 className="font-semibold text-sm">Geçmiş Analizler</h3>
          <span className="text-[11px] text-[#94A3B8]">{jobs.length} kayıt</span>
        </div>
        {loading && <div className="p-10 text-center text-sm text-[#94A3B8]"><Loader2 className="animate-spin inline mr-2" size={14}/>Yükleniyor…</div>}
        {!loading && jobs.length === 0 && (
          <div className="p-12 text-center">
            <Search size={32} className="mx-auto text-[#CBD5E1] mb-3"/>
            <div className="text-sm font-medium text-[#0F172A]">Henüz analiz yok</div>
            <p className="text-xs text-[#94A3B8] mt-1 mb-4">İlk CSV'ni yükleyerek başla.</p>
            <button onClick={() => nav("/ai-araclar/anahtar-kelime/yeni")} className="ky-btn ky-btn-outline ky-btn-sm">
              <Plus size={12}/>Yeni Analiz Oluştur
            </button>
          </div>
        )}
        {!loading && jobs.map(j => {
          const st = STATUS[j.status] || STATUS.uploaded;
          const Icon = st.icon;
          const inProgress = ["preparing","analyzing","finalizing"].includes(j.status);
          return (
            <div key={j.id} className="px-5 py-3 border-b border-[#ECECEF] last:border-b-0 hover:bg-[#FAFAFB] flex items-center gap-3" data-testid={`kw-job-${j.id}`}>
              <div onClick={() => nav(`/ai-araclar/anahtar-kelime/${j.id}`)} className="flex-1 min-w-0 cursor-pointer">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium truncate">{j.filename}</span>
                  <span className={`ky-badge ${st.color} text-[10px]`}><Icon size={10} className={inProgress?"animate-spin":""}/>{st.label}</span>
                  {j.mode === "search_terms" && <span className="ky-badge ky-badge-neutral text-[10px]">Arama Terimleri</span>}
                  {j.mode === "planner" && <span className="ky-badge ky-badge-neutral text-[10px]">Keyword Planner</span>}
                  {j.published_to_portal && <span className="ky-badge ky-badge-success text-[10px]"><Globe2 size={9}/>Portal'da</span>}
                </div>
                <div className="text-[11px] text-[#94A3B8] truncate">
                  {j.customer ? `${j.customer.name} · ` : ""}
                  {j.total_rows} satır
                  {j.result?.positive_keywords && ` · ${j.result.positive_keywords.length} pozitif · ${j.result.negative_keywords.length} negatif`}
                  {j.cost?.usd ? ` · $${j.cost.usd.toFixed(4)}` : ""}
                  {" · "}
                  {new Date(j.created_at).toLocaleString("tr-TR")}
                </div>
                {inProgress && (
                  <div className="mt-1.5 h-1 bg-[#F4F4F5] rounded-full overflow-hidden">
                    <div className="h-full bg-[#4F46E5] transition-all" style={{ width: `${j.progress || 0}%` }}/>
                  </div>
                )}
                {j.status_message && inProgress && <div className="text-[10px] text-[#64748B] mt-1">{j.status_message}</div>}
              </div>
              <button onClick={() => nav(`/ai-araclar/anahtar-kelime/${j.id}`)} className="ky-btn ky-btn-ghost ky-btn-icon" title="Aç">
                <ChevronRight size={14}/>
              </button>
              <button onClick={() => remove(j.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]" title="Sil">
                <Trash2 size={13}/>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
